// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/';
import useValidateForm from '@Hooks/useValidateForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import CaseMerge, { CaseMergeFormValues } from '@Organisms/Case/CaseMerge/CaseMerge';
import { useGetSettings } from '@Queries/UISettingsQueries';
import React, { ReactElement } from 'react';
import { CaseOutput, FilterInput } from 'thehive-sdk';

import './AlertMerge.scss';

interface AlertMergeDrawerProps {
    onMergeAlertsIntoCase: (caseId: string) => Promise<any>;
    triggerElem: ReactElement;
    listCasesForMerge: (filter: FilterInput) => Promise<CaseOutput[]>;
}

const AlertMergeDrawer = ({
    onMergeAlertsIntoCase,
    triggerElem,
    listCasesForMerge,
}: AlertMergeDrawerProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinish = ({ caseData }: CaseMergeFormValues): Promise<any> => {
        return onMergeAlertsIntoCase(caseData?._id || '');
    };

    const { data: settingsData } = useGetSettings();

    const disallowMerge = !!settingsData?.disallowMergeAlertInResolvedSimilarCases;

    return (
        <THDrawer
            title={i('alert.merge.title')}
            triggerElem={triggerElem}
            className="drawer-form"
            afterVisibleChange={() => form.resetFields()}
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <CaseMerge
                    form={form}
                    disabled={!isValid || !fieldsTouched}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    onFinish={(values) =>
                        onFinish(values).finally(() => {
                            toggleDrawer();
                            setFieldsTouched(false);
                        })
                    }
                    onCancel={() => {
                        setFieldsTouched(false);
                        toggleDrawer();
                        form.resetFields();
                    }}
                    listCasesForMerge={listCasesForMerge}
                    disallowMerge={disallowMerge}
                />
            )}
        </THDrawer>
    );
};

export default AlertMergeDrawer;
